import { IPagination, ISearch } from "../../data/AppInterface";
import { TReactSetState, TSetPaginationFn } from "../../data/AppType";
import {
  GET_ALL_INSIGHTS,
  GET_ALL_INSIGHTS_BY_ID,
  GET_ALL_INSIGHTS_CATEGORY_PUBLIC,
} from "../../services/Endpoints";
import {
  DEFAULT_STATUS_CODE_SUCCESS,
  MESSAGE_UNKNOWN_ERROR_OCCURRED,
} from "../../utils/AppConstants";
import {
  generatePaginationFromApiRes,
  resetPaginationWithPpr,
  sweetAlertError,
  toastError,
} from "../../utils/AppFunctions";

export interface IInsightsCategory {
  id: number;
  category_title: string;
  category_meta_title: string;
  parent_category_id?: number;
  slug: string;
  is_active?: string;
  deleted?: string;
}
export interface IInsights {
  id: number;
  blog_title: string;
  blog_meta_title: string;
  blog_content: string;
  slug: string;
  published: string;
  publish_date?: Date;
  expiry_date?: Date;
  blog_categories: string;
  post_anonymously: string;
  cover_image: string;
  hash_tags: string[];
  app_media: IMediaItemImage[];
  is_active?: string;
  blog_tags?: string[];
  blog_liked?: string;
  total_reviews?: string;
  created_date?: Date;
  created_by?: string;
  profile_url?: string;
}
export interface IInsightsDetail {
  id: number;
  blog_title: string;
  blog_meta_title: string;
  blog_content: string;
  slug: string;
  published: string;
  publish_date?: Date;
  expiry_date?: Date;
  blog_categories: IBlogCategory[] | null;
  post_anonymously: string;
  cover_image: string;
  hash_tags: string[];
  app_media: IMediaItemImage[];
  is_active?: string;
  blog_tags?: string[];
  blog_liked?: string;
  total_reviews?: string;
  created_date?: Date;
  created_by?: string;
  profile_url?: string;
}
export interface IMediaItemImage {
  media_type: string;
  media_url: string;
  media_title?: string;
}
export interface IBlogCategory {
  id: number;
  category_title: string;
}
export const fetchInsightsCategoryApiCall = async (
  setLoading: TReactSetState<boolean>,
  setInsightsCategoryList: TReactSetState<IInsightsCategory[]>,
  setPagination: TSetPaginationFn,
  pagination: IPagination,
  setCid: TReactSetState<number | null>,
  PCid?: number
) => {
  try {
    setLoading(true);
    const insightCategoryList = await GET_ALL_INSIGHTS_CATEGORY_PUBLIC(
      pagination
    );
    if (
      insightCategoryList &&
      insightCategoryList.code === DEFAULT_STATUS_CODE_SUCCESS
    ) {
      if (insightCategoryList.data) {
        setInsightsCategoryList(insightCategoryList.data.item);
        if (insightCategoryList.data.item.length > 0) {
          const foundCategory = insightCategoryList.data.item.find(
            (category: IInsightsCategory) => category.id === PCid
          );
          if (foundCategory) {
            PCid && setCid(PCid);
          } else {
            setCid(null);
          }
        }
      } else {
        setInsightsCategoryList([]);
      }
    } else {
      toastError(insightCategoryList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
    }
  } catch (e: any) {
    toastError(
      e?.insightCategoryList?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED
    );
  } finally {
    setLoading(false);
  }
};

export const fetchInsightsApiCall = async (
  setInsightsList: TReactSetState<IInsights[]>,
  setLoading: TReactSetState<boolean>,
  setPagination: TSetPaginationFn,
  pagination: IPagination,
  insights: IInsights[],
  setLoader: TReactSetState<boolean>,
  search?: ISearch,
  id?: number | null
) => {
  try {
    if (!insights) setLoading(true);
    else {
      setLoader(true);
      let insightsList;
      insightsList = await GET_ALL_INSIGHTS(pagination, { id, search });

      if (insightsList && insightsList.code === DEFAULT_STATUS_CODE_SUCCESS) {
        if (id) setInsightsList(insightsList?.data?.item);
        else setInsightsList(insightsList?.data?.item);
        setPagination({
          ...pagination,
          ...generatePaginationFromApiRes(insightsList.data.pagination),
        });
      } else {
        setInsightsList([]);
        setPagination({
          ...pagination,
          ...resetPaginationWithPpr(pagination.per_page_rows),
        });
      }
    }
  } catch (error: any) {
    toastError(error?.insightsList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } finally {
    setLoading(false);
    setLoader(false);
  }
};

export const fetchExploreInsightsApiCall = async (
  setInsightsList: TReactSetState<IInsights[]>,
  setLoading: TReactSetState<boolean>,
  setPagination: TSetPaginationFn,
  pagination: IPagination,
  insights: IInsights[],
  setLoader: TReactSetState<boolean>,
  id: number | null,
  blog_id: number | undefined
) => {
  try {
    setLoader(true);

    let insightsList = await GET_ALL_INSIGHTS(pagination, { id });
    if (insightsList && insightsList.code === DEFAULT_STATUS_CODE_SUCCESS) {
      if (insightsList.data.item.length > 1) {
        const filteredList = insightsList.data.item.filter(
          (item: IInsights) => item.id !== blog_id
        );
        setInsightsList(filteredList);
        setPagination({
          ...pagination,
          ...generatePaginationFromApiRes(insightsList.data.pagination),
        });
      } else {
        const trendFlag = true;
        insightsList = await GET_ALL_INSIGHTS(pagination, { id, trendFlag });
        const filteredList =
          insightsList &&
          insightsList.data.length > 0 &&
          insightsList.data.item.filter(
            (item: IInsights) => item.id !== blog_id
          );
        setInsightsList(filteredList);
        setPagination({
          ...pagination,
          ...generatePaginationFromApiRes(insightsList.data.pagination),
        });
      }
    } else {
      setInsightsList([]);
      setPagination({
        ...pagination,
        ...resetPaginationWithPpr(pagination.per_page_rows),
      });
    }
  } catch (error: any) {
    toastError(error?.insightsList.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  } finally {
    setLoading(false);
    setLoader(false);
  }
};
export const fetchBlogById = async (
  id: number | undefined,
  setBlogsFormInitialValues: TReactSetState<IInsights | undefined>
) => {
  try {
    if (id) {
      const blogById = await GET_ALL_INSIGHTS_BY_ID(id);
      if (blogById && blogById.code === DEFAULT_STATUS_CODE_SUCCESS) {
        setBlogsFormInitialValues(blogById.data);
      } else {
        sweetAlertError(blogById?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
      }
    }
  } catch (e: any) {
    sweetAlertError(e?.blogById?.message || MESSAGE_UNKNOWN_ERROR_OCCURRED);
  }
};
